/**
 * usePostureBrief — derives the editorial copy block at the top of the
 * Command Center from the active posture state.
 *
 * Phase 11 wires real templated copy into the standfirst. The hook signature
 * stays stable for callers — Phase 11 only widens the optional inputs so the
 * Dashboard can pass through delta + topAction + criticalCount when they're
 * available. Existing callers that pass only `{score, pillarScores}` keep
 * working unchanged.
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composePostureBrief,
  type Pillar,
  type PostureBrief,
  type PostureBriefPillarScores,
} from '@/lib/posture-brief'

export interface UsePostureBriefArgs {
  /**
   * Active tenant ID. Combined with the local day to seed editorial variant
   * selection so the brief stays stable for that tenant across the day.
   */
  tenantId?: string
  /** Current Guardian Score (0-100). */
  score?: number | null
  /** Pillar component scores. */
  pillarScores?: PostureBriefPillarScores
  /** 30-day score delta — drives the green pill and the headline modifier. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact priority action — informs the verdict + deck risk slot. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity issue count — drives the weak-link clause. */
  criticalCount?: number
}

export function usePostureBrief(args: UsePostureBriefArgs = {}): PostureBrief {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoize on the value-shape rather than the object identity so the
  // Dashboard can rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionImpact = args.topAction?.impact

  return useMemo(
    () =>
      composePostureBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        pillarScores: args.pillarScores,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? { pillar: topActionPillar, title: topActionTitle, impact: topActionImpact }
            : undefined,
        criticalCount: args.criticalCount,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      args.pillarScores,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionImpact,
      args.criticalCount,
    ],
  )
}
